import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { endOfDay, startOfDay } from 'date-fns';
import { Box, Stack, Typography } from '@mui/material';

import DateSelectTooltip from '@/components/common/date/DateRangeSelectTooltip';
import { ctxTachograph, TachographContextType } from '@/contexts/TachographCtx';
import TachographCategorySelector from './TachographCategorySelector';
import { ctxUser, UserContext } from '@/contexts/UserContext';
import TachographTargetTab from './TachographTargetTab';
import TachographDriverTab from './TachographDriverTab';
import { Popup } from '@/components';
import { TachographMobileHeader } from './TachographMobileHeader';
import { TachographMobileBody } from './TachographMobileBody';

import './TachographPopup.css';

export type TachoTabs = 'TargetTab' | 'DriverTab' | 'undefined';

function TachographDesktop({
  activeTab,
  setActiveTab,
  validDate,
  tachographCtx,
  userCtx,
}: {
  activeTab: TachoTabs;
  setActiveTab: (tab: TachoTabs) => void;
  validDate: boolean;
  tachographCtx: TachographContextType;
  userCtx: UserContext | undefined;
}) {
  const { t } = useTranslation();

  const resetContext = useCallback(() => {
    tachographCtx?.setSelectedRowIds([]);
    tachographCtx?.setSelectedRowCardNumbers([]);
    tachographCtx?.setSearchText(undefined);
  }, [tachographCtx]);

  useEffect(() => {
    if (!userCtx || !tachographCtx) return;
    resetContext();
    setActiveTab('TargetTab');
  }, [userCtx]);

  if (!tachographCtx) return;

  function onAccept(newFrom: Date | undefined, newTo: Date | undefined) {
    if (!newFrom || isNaN(newFrom.getTime()) || !newTo || isNaN(newTo.getTime())) {
      // TODO ezt nem kellene context-ba tenni, csak siman atadni
      tachographCtx?.setSelectedDates(undefined);
      return;
    }
    const from = startOfDay(newFrom);
    const to = endOfDay(newTo);
    tachographCtx?.setSelectedDates({ from, to });
  }

  function updateActiveTab(tab: TachoTabs) {
    resetContext();
    setActiveTab(tab);
  }

  return (
    <>
      <div id="tachograph-main-container" data-qa-element-id="tachograph_file_download_main_panel">
        <div className="tachograph-date-selector-container" data-qa-element-id="date_selector_panel">
          <Box sx={{ pt: 2 }} data-qa-element-id="date_selector_panel">
            <Typography variant="h6" data-qa-element-id="date_selector_panel_title">
              {t('tachograph.dateSelector.title')}
            </Typography>
            <Typography
              variant="helperText"
              className="tachograph-header-description"
              data-qa-element-id="date_selector_panel_helper_label"
            >
              {t('tachograph.dateSelector.description')}
            </Typography>
          </Box>
          <DateSelectTooltip validDate={validDate} onAccept={onAccept} selectedDates={tachographCtx.selectedDates} />
        </div>

        <div
          className={`tachograph-main-container ${validDate ? '' : 'disabled'}`}
          inert={!validDate}
          data-qa-element-id="tachograph_file_download_category_selector_panel"
        >
          <Stack direction="row" data-qa-element-id="category_selector_panel">
            <Box sx={{ pt: 2 }}>
              <Typography variant="h6" data-qa-element-id="category_selector_panel_title">
                {t('tachograph.category.title')}
              </Typography>
              <Typography
                variant="helperText"
                className="tachograph-header-description"
                data-qa-element-id="category_selector_panel_helper_text"
              >
                {t('tachograph.category.description')}
              </Typography>
            </Box>
            <TachographCategorySelector activeTab={activeTab} setActiveTab={updateActiveTab} />
          </Stack>
          {activeTab === 'TargetTab' && <TachographTargetTab activeTab={activeTab} />}
          {activeTab === 'DriverTab' && <TachographDriverTab activeTab={activeTab} />}
        </div>
      </div>
    </>
  );
}

export default function TachographPopup({
  closePopup,
  isBottom,
}: {
  closePopup: () => Promise<void>;
  isBottom: boolean;
}) {
  const tachographCtx = useContext(ctxTachograph);
  const userCtx = useContext(ctxUser);
  const [validDate, setValidDate] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<TachoTabs>('undefined');

  useEffect(() => {
    const dateInvalid =
      tachographCtx?.selectedDates == undefined ||
      tachographCtx?.selectedDates.from == undefined ||
      tachographCtx?.selectedDates.to == undefined;
    setValidDate(!dateInvalid);
  }, [tachographCtx?.selectedDates]);

  useEffect(() => {
    if (!userCtx || !tachographCtx) return;
    const email = userCtx.user.email;
    if (email) {
      tachographCtx.setAddressee(email);
    }
  }, [userCtx]);

  if (!tachographCtx || !closePopup) return null;
  return isBottom ? (
    <Popup
      popupTitle={undefined}
      header={<TachographMobileHeader activeTab={activeTab} />}
      body={<TachographMobileBody activeTab={activeTab} setActiveTab={setActiveTab} />}
      footer={undefined}
      closePopup={closePopup}
      showCloseButton={false}
      isFullScr={true}
    />
  ) : (
    <TachographDesktop
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      validDate={validDate}
      tachographCtx={tachographCtx}
      userCtx={userCtx}
    />
  );
}
